/**
 * The geography, all of it, in one file with no network and no server.
 *
 * This is where the map feature's central constraint shows itself. The
 * coordinates live inside a content blob, so the server cannot answer a
 * geographic question even if it wanted to — and it never could have anyway:
 * no browser reports a position to anybody while the phone is in a pocket
 * (SPEC §4.10). So «далеко ли», «что рядом» and «откуда эта ссылка» are all
 * arithmetic on the device, which is what this module is.
 */

export interface Point {
  lat: number;
  lng: number;
}

/** Earth, as a sphere. Good to a few metres over the distances a city has. */
const EARTH_RADIUS_M = 6_371_008.8;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * How far apart two points are, in metres.
 *
 * The haversine formula: a sphere rather than the ellipsoid the planet
 * actually is, which is wrong by about a part in five hundred — a metre and a
 * half across a kilometre. Nothing this screen says is that precise; «в 200
 * метрах» is the claim, and it survives.
 */
export function distanceMetres(a: Point, b: Point): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function isPoint(value: unknown): value is Point {
  if (typeof value !== "object" || value === null) return false;
  const { lat, lng } = value as Partial<Point>;
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

function point(lat: number, lng: number): Point | null {
  const candidate = { lat, lng };
  return isPoint(candidate) ? candidate : null;
}

/**
 * A pair of numbers out of a string, in the order given.
 *
 * Both separators are accepted because both are in the wild: a comma between
 * them, and whitespace after it or instead of it.
 */
function pair(text: string): [number, number] | null {
  const match = text.match(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/);
  if (!match) return null;

  const first = Number(match[1]);
  const second = Number(match[2]);
  return Number.isFinite(first) && Number.isFinite(second) ? [first, second] : null;
}

/**
 * The point a pasted string refers to, or null.
 *
 * Every map service writes coordinates into its URLs; none of them agree on
 * how. The rules below are read in order of how specific they are, because the
 * general ones would misread the specific ones — Google's `@lat,lng` sits in
 * the path of a URL that may also carry `ll=` meaning something else.
 *
 * The **order of the two numbers is the whole difficulty**: Yandex writes
 * longitude first, Google writes latitude first, and inside Moscow both are
 * plausible latitudes. Nothing can tell them apart from the numbers alone, so
 * each rule is tied to the parameter that names its convention, and a string
 * with no such parameter is read as `lat, lng` — which is what a person
 * copying a pair out of anywhere means by it.
 *
 * A short link (`yandex.ru/maps/-/…`, `goo.gl/maps/…`) carries no coordinates
 * at all: it is a key to somebody else's database, and following it would be a
 * request to them. Those come back null, and the screen says so.
 */
export function parseCoordinates(input: string): Point | null {
  const text = input.trim();
  if (!text) return null;

  // geo:55.75,37.61 — the platform's own URI, latitude first by RFC 5870.
  const geo = text.match(/^geo:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);
  if (geo) return point(Number(geo[1]), Number(geo[2]));

  /*
   * Google's place data: `!3d<lat>!4d<lng>`.
   *
   * Read before `@…`, because a Google place URL has both and they are not the
   * same point — the `@` is where the camera was, this is where the place is.
   */
  const google = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (google) return point(Number(google[1]), Number(google[2]));

  // Google's camera: /@lat,lng,17z
  const at = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) return point(Number(at[1]), Number(at[2]));

  // Yandex: ll= and pt= are longitude first. So is 2GIS's path segment.
  const yandex = text.match(/[?&](?:ll|pt|whatshere\[point\])=(-?\d+(?:\.\d+)?)(?:%2C|,)(-?\d+(?:\.\d+)?)/i);
  if (yandex) return point(Number(yandex[2]), Number(yandex[1]));

  const twogis = text.match(/2gis\.[a-z]+\/[^?#]*?\/(-?\d+\.\d+)%2C(-?\d+\.\d+)/i)
    ?? text.match(/2gis\.[a-z]+\/[^?#]*?\/(-?\d+\.\d+),(-?\d+\.\d+)/i);
  if (twogis) return point(Number(twogis[2]), Number(twogis[1]));

  // A search query somebody handed a coordinate to: q=lat,lng.
  const query = text.match(/[?&](?:q|query|daddr|destination)=(-?\d+(?:\.\d+)?)(?:%2C|,)\s*(-?\d+(?:\.\d+)?)/i);
  if (query) return point(Number(query[1]), Number(query[2]));

  /*
   * A bare pair, latitude first — but only when the string is nothing else.
   *
   * A URL that reached here has already failed every rule that knows what its
   * numbers mean, and guessing at whatever pair of decimals it happens to
   * contain would put pins in the sea.
   */
  if (/^https?:|^[a-z-]+:\/\//i.test(text)) return null;

  const bare = pair(text);
  return bare ? point(bare[0], bare[1]) : null;
}

/**
 * Places within `radius` of a point, nearest first.
 *
 * Takes anything with a `point`, so both the map's views and a raw row fit.
 * Ones with no coordinates — an encrypted space with no key on this device —
 * are not near anything and drop out.
 */
export function nearby<T extends { point: Point | null }>(
  items: readonly T[],
  from: Point,
  radiusMetres: number,
): Array<T & { distance: number }> {
  return items
    .flatMap((item) =>
      item.point ? [{ ...item, distance: distanceMetres(from, item.point) }] : [],
    )
    .filter((item) => item.distance <= radiusMetres)
    .sort((a, b) => a.distance - b.distance);
}

/**
 * A distance as a person would say it.
 *
 * Metres below a kilometre, rounded to fifty — a phone's own fix is good to
 * about that, and «в 237 метрах» claims a precision nobody has. Kilometres
 * above it, with one decimal until ten and none after.
 */
export function formatDistance(metres: number): { value: number; unit: "m" | "km" } {
  if (metres < 1_000) {
    return { value: Math.max(0, Math.round(metres / 50) * 50), unit: "m" };
  }

  const km = metres / 1_000;
  return { value: km < 10 ? Math.round(km * 10) / 10 : Math.round(km), unit: "km" };
}

/**
 * A link that opens the place in whatever map app the phone has.
 *
 * `geo:` is the honest answer on Android — the system asks which app, and the
 * one the person already uses wins, with its traffic and its voice. iOS has no
 * such scheme, so it gets Apple Maps' own, which the system routes the same
 * way. Neither goes through us: this app has no business knowing where
 * somebody actually went.
 */
export function directionsUrl(place: Point, label: string, platform: string): string {
  const coordinates = `${place.lat},${place.lng}`;
  const name = encodeURIComponent(label);

  if (/iphone|ipad|ipod|mac/i.test(platform)) {
    return `https://maps.apple.com/?daddr=${coordinates}&q=${name}`;
  }

  return `geo:${coordinates}?q=${coordinates}(${name})`;
}

/* --------------------------------------------------------------- clustering */

/** One pin standing for one or more places. */
export interface Cluster<T> {
  /** Stable while the same places are grouped together, so markers survive. */
  key: string;
  lat: number;
  lng: number;
  items: T[];
}

/**
 * Group places that would land on top of each other on screen.
 *
 * By pixels rather than by metres, because overlap is a property of the screen
 * and not of the world: two doors on one street are the same dot at the zoom
 * where a city fits, and comfortably apart three zooms in. So the caller hands
 * in the map's own projection, and the same places regroup as it changes.
 *
 * Greedy and single-pass. A proper k-means would move the centres around until
 * they settled, and for a couple's list of places it would move them to the
 * same answer a great deal more slowly — while producing a *different* answer
 * on every pan, which is the one thing a marker must not do.
 */
export function clusterByPixels<T extends { id: string; point: Point | null }>(
  items: readonly T[],
  project: (point: Point) => { x: number; y: number },
  radius: number,
): Array<Cluster<T>> {
  const clusters: Array<Cluster<T> & { x: number; y: number }> = [];

  for (const item of items) {
    // Somewhere this device cannot read is nowhere: it belongs to no group and
    // gets no pin. The list still carries it.
    if (!item.point) continue;

    const at = project(item.point);
    const near = clusters.find(
      (cluster) => Math.hypot(cluster.x - at.x, cluster.y - at.y) <= radius,
    );

    if (!near) {
      clusters.push({ key: item.id, lat: item.point.lat, lng: item.point.lng, items: [item], x: at.x, y: at.y });
      continue;
    }

    near.items.push(item);
    // The centre moves to the middle of what is in it, so a group of three
    // sits among its three rather than on the first one that arrived.
    const count = near.items.length;
    near.lat += (item.point.lat - near.lat) / count;
    near.lng += (item.point.lng - near.lng) / count;
    near.x += (at.x - near.x) / count;
    near.y += (at.y - near.y) / count;
  }

  return clusters.map((cluster) => ({
    key: cluster.items
      .map((item) => item.id)
      .sort()
      .join(":"),
    lat: cluster.lat,
    lng: cluster.lng,
    items: cluster.items,
  }));
}
